import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, Permissions } from '../types';

const API = '/api';
const TOKEN_KEY = 'cobrand_token';

const ADMIN_PERMISSIONS: Permissions = {
  production_advance: true, production_delay: true, production_create: true,
  inventory_in: true, inventory_out: true, report_view: true,
};
const DEFAULT_PERMISSIONS: Permissions = {
  production_advance: false, production_delay: false, production_create: true,
  inventory_in: true, inventory_out: true, report_view: true,
};

interface AuthContextType {
  user: User | null;
  token: string | null;
  permissions: Permissions;
  can: (perm: keyof Permissions) => boolean;
  login: (email: string, password: string) => Promise<{ ok: boolean; error?: string }>;
  logout: () => void;
  isAuthenticated: boolean;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user,        setUser]        = useState<User | null>(null);
  const [token,       setToken]       = useState<string | null>(null);
  const [permissions, setPermissions] = useState<Permissions>(DEFAULT_PERMISSIONS);
  const [loading,     setLoading]     = useState(true);

  const loadPermissions = async (userId: number, role: string, tkn: string) => {
    if (role === 'admin') { setPermissions(ADMIN_PERMISSIONS); return; }
    try {
      const res = await fetch(`${API}/users/${userId}/permissions`, {
        headers: { Authorization: `Bearer ${tkn}` },
      });
      if (res.ok) setPermissions(await res.json());
    } catch { /* 기본값 유지 */ }
  };

  /* ── 앱 시작 시 저장된 토큰으로 자동 로그인 ── */
  useEffect(() => {
    const saved = localStorage.getItem(TOKEN_KEY);
    if (!saved) { setLoading(false); return; }

    fetch(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${saved}` },
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(async (data: User) => {
        setToken(saved);
        await loadPermissions(data.id, data.role, saved);
        setUser(data);
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);   // 만료된 토큰 제거
      })
      .finally(() => setLoading(false));
  }, []);

  /* ── 로그인 ── */
  const login = async (
    email: string,
    password: string,
  ): Promise<{ ok: boolean; error?: string }> => {
    try {
      const res = await fetch(`${API}/auth/login`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email: email.trim(), password }),
      });

      const data = await res.json();

      if (!res.ok) {
        return { ok: false, error: data.error ?? '로그인에 실패했습니다.' };
      }

      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setUser(data.user);
      await loadPermissions(data.user.id, data.user.role, data.token);
      return { ok: true };
    } catch {
      return { ok: false, error: '서버에 연결할 수 없습니다.' };
    }
  };

  /* ── 로그아웃 ── */
  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
    setPermissions(DEFAULT_PERMISSIONS);
  };

  const can = (perm: keyof Permissions) =>
    user?.role === 'admin' || !!permissions[perm];

  return (
    <AuthContext.Provider value={{ user, token, permissions, can, login, logout, isAuthenticated: !!user, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

/* ── API 요청에 인증 토큰을 자동으로 붙여주는 헬퍼 ── */
export function authFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const token = localStorage.getItem(TOKEN_KEY);
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
}
